import { func, string } from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toastWarning } from '@components/Toast';

import DateRangeRightSidePanel from '@features/filter/v2/salesFunnel/DateRangeRightSidePanel';

import {
	clearGlobalFilter,
	storeItemInGlobalFilter,
	storeSelectedFilterCheckboxType
} from '@store/actions/FilterAction';
import {
	setExploreProductsSourceType,
	setTrendResearchSelectedGroup,
	setTrendResearchSelectedSort
} from '@store/actions/trend-research/TrendResearchAction';
import {
	useExploreProductsSource,
	useSelectedGroup,
	useSelectedSort
} from '@store/reducers/trend-research/TrendResearchReducer';

import { trackMixPanelEvent } from '@services/commonService';

import { trendResearchDateRangePresets } from '@utils/Util';

import CommonImage from '@/components/CommonImage';

const GROUP_BY_LIST = [
	{ label: 'Group by', value: 'DEFAULT' },
	{ label: 'By fabric', value: 'FABRIC' },
	{ label: 'By color', value: 'COLOR' },
	{ label: 'By theme', value: 'THEME' },
	{ label: 'By graphics', value: 'GRAPHICS' },
	{ label: 'By prints', value: 'AOP' }
];

const SORT_BY_LIST = [
	{ label: 'New Arrival', value: 'NEW_ARRIVAL' },
	{ label: 'Bestseller', value: 'BEST_SELLER' }
];

const TrendTopFilter = ({ type, title = '', toggleImageSearchModal }) => {
	const dispatch = useDispatch();
	const selectedGroup = useSelectedGroup();
	const selectedSort = useSelectedSort();
	const filterData = useSelector((store) => store.filterFieldStore);
	const selectedField = useSelector((store) => store.slectedCheckBoxFieldStore);
	const exploreProductSource = useExploreProductsSource();
	const [debounceValue, setDebounceValue] = React.useState('');

	const handleSearch = (e) => {
		const value = e.target.value;
		setDebounceValue(value);
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter' || e.type === 'blur') {
			const value = e.target.value;
			if (selectedGroup !== 'DEFAULT') {
				dispatch(setTrendResearchSelectedGroup('DEFAULT'));
			}
			let data = {
				name: 'trendResearchSearch',
				type: 'INPUT_VALUE',
				key: value,
				value: value,
				identifier: 'trendResearchSearch'
			};
			dispatch(storeItemInGlobalFilter(data));
			trackMixPanelEvent({ searchQuery: value }, 'TRENDS_TEXT_SEARCH');
		}
	};

	useEffect(() => {
		const searchValue = filterData?.trendResearchSearch?.[0]?.value || '';
		setDebounceValue(searchValue);
	}, [filterData?.trendResearchSearch]);

	const toggleCalendar = () => {
		if (selectedField?.selectedCheckboxField === 'DATE_RANGE') {
			dispatch(storeSelectedFilterCheckboxType(null));
		} else {
			dispatch(storeSelectedFilterCheckboxType('DATE_RANGE'));
		}
	};

	const setProductsSource = (data) => {
		dispatch(setExploreProductsSourceType(data));
	};

	const isBrandFilterSelected = () => {
		const brandFilter = filterData?.brandCodeList?.length > 0;
		const fabricFilter = filterData?.supplierCategoryList?.length > 0;
		const compositionFilter = filterData?.keyCompositionList?.length > 0;
		const fiberFilter = filterData?.fiberList?.length > 0;
		const searchFilter = filterData?.trendResearchSearch?.length > 0;

		return fabricFilter || brandFilter || compositionFilter || fiberFilter || searchFilter;
	};

	const isInstagramFilterSelected = () => {
		return filterData?.userNameList?.length > 0;
	};

	const onClickTabChange = (source) => {
		if (source === 'BRAND') {
			if (!isInstagramFilterSelected()) {
				setProductsSource('BRAND');
				trackMixPanelEvent({}, 'TRENDS_TAB_ON_CHANGE');
			} else {
				toastWarning('Please clear the influencer related filter to explore Brand');
			}
		} else if (source === 'INSTAGRAM') {
			if (!isBrandFilterSelected()) {
				if (selectedGroup !== 'FABRIC') {
					setProductsSource('INSTAGRAM');
					trackMixPanelEvent({}, 'TRENDS_TAB_ON_CHANGE');
				} else {
					toastWarning('Group By Fabric is not available for Instagram');
				}
			} else {
				toastWarning('Please clear the brand related filter to explore Instagram');
			}
		}
	};

	return (
		<div
			className={`section-header d-flex flex-wrap align-items-center justify-content-between ${
				type !== 'Explore' ? 'pb-3' : ''
			}`}
		>
			<div>
				<p className="semibold-16 gray_dark_01">{title}</p>
				<div className="inner-tabs">
					<ul>
						{['BRAND', 'INSTAGRAM'].map((source) => (
							<li
								key={source}
								className={`weight-400 ${exploreProductSource === source ? 'active' : ''}`}
								onClick={() => onClickTabChange(source)}
							>
								{source === 'BRAND' ? 'Brands' : 'Instagram'}
							</li>
						))}
					</ul>
				</div>
			</div>

			<div className="product-search-action d-flex flex-wrap align-items-center align-self-center">
				<div className="sort-button mr-1">
					<select
						className="border-0"
						onChange={(e) => {
							// trackMixPanelEvent({ sortType: e.target.value }, 'TRENDS_SORT_BY_PRICE');
							dispatch(setTrendResearchSelectedSort(e.target.value));
						}}
						defaultValue={selectedSort}
					>
						{SORT_BY_LIST?.map((item) => (
							<option
								key={item.value}
								value={item.value}
								selected={selectedSort === item.value}
							>
								{item.label}
							</option>
						))}
					</select>
				</div>

				<div className="sort-button mr-1">
					<select
						className="border-0"
						onChange={(e) => {
							if (!filterData?.trendResearchSearch?.[0]?.key) {
								trackMixPanelEvent(
									{ groupType: e.target.value },
									'TRENDS_GROUP_BY_ON_CHANGE'
								);
								dispatch(setTrendResearchSelectedGroup(e.target.value));
							} else {
								toastWarning('Please clear the search filter to change the group by');
							}
						}}
						value={selectedGroup}
					>
						{GROUP_BY_LIST?.map((item) => {
							if (exploreProductSource === 'INSTAGRAM' && item.value === 'FABRIC') {
								return <React.Fragment key={item.value}></React.Fragment>;
							} else {
								return (
									<option
										key={item.value}
										value={item.value}
										selected={selectedGroup === item.value}
									>
										{item.label}
									</option>
								);
							}
						})}
					</select>
				</div>
				{exploreProductSource === 'BRAND' && (
					<div className="search-filter product-search-filter mr-3">
						<div className="with-icon right-icon">
							<input
								type="text"
								placeholder="Search for Products"
								onChange={handleSearch}
								value={debounceValue}
								onKeyPress={handleKeyPress}
								onBlur={handleKeyPress}
							/>
							<span className="right-icon cursor-pointer">
								<CommonImage
									src="/icons/camera.svg"
									onClick={() => toggleImageSearchModal(true)}
								/>
							</span>
							<span className="left-icon cursor-pointer">
								<CommonImage src="/icons/search.png" />
							</span>
						</div>
					</div>
				)}
				{exploreProductSource === 'INSTAGRAM' && (
					<div className="search-img cursor-pointer mr-3">
						<CommonImage src="/icons/camera.svg" onClick={() => toggleImageSearchModal(true)} />
					</div>
				)}
				<div className="calender pb-2">
					<div className="cursor-pointer d-flex align-items-center justify-content-between">
						<div
							onClick={toggleCalendar}
							className="w-100 cursor-pointer d-flex align-items-center justify-content-between"
						>
							<p className="regular-14 gray_dark_02 weight-400 pr-2">
								{filterData['dateRange']?.[0]?.value}
							</p>
							{!filterData['dateRange']?.[0]?.value && (
								<CommonImage src="/icons/calendar.svg" alt="calender" />
							)}
						</div>

						{filterData['dateRange']?.[0]?.value && (
							<CommonImage
								className="cursor-pointer"
								src="/icons/close.svg"
								alt="date-range"
								onClick={() => dispatch(clearGlobalFilter())}
							/>
						)}
					</div>
					{selectedField?.selectedCheckboxField === 'DATE_RANGE' && (
						<div className="trending-calendar shadow-8dp p-3">
							<DateRangeRightSidePanel
								type="DATE_RANGE"
								presets={trendResearchDateRangePresets}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

TrendTopFilter.propTypes = {
	type: string.isRequired,
	title: string,
	toggleImageSearchModal: func
};

export default TrendTopFilter;
